import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { Eye, EyeOff, Phone, User, Lock, ChevronDown, Camera, MapPin, CheckCircle, Clock } from 'lucide-react';

const USERS_DATA = [
  { name: "Shashi Kumar K", password: "Shashi@DG2025", mobile: "9035224169" },
  { name: "M Pavan Kumar", password: "Pavan@DG2025", mobile: "9019589074" },
  { name: "Shivaraj N", password: "Shivaraj@DG2025", mobile: "9880051298" },
  { name: "Hanish S.S", password: "Hanish@DG2025", mobile: "9449201025" }
];

// --- IDENTITY VERIFICATION COMPONENT ---
const IdentityVerification = ({ user, onLogout }) => {
  const [isWindowOpen, setIsWindowOpen] = useState(false);
  const [image, setImage] = useState(null);
  const [location, setLocation] = useState({ lat: null, lng: null, loading: true });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const checkTime = () => {
      const now = new Date();
      const hours = now.getHours();
      const mins = now.getMinutes();
      const validHours = [11, 12, 13, 14, 15, 16, 17];
      // Returns true if hour is valid and within first 15 mins
      setIsWindowOpen(validHours.includes(hours) && mins >= 0 && mins <= 15);
    };

    checkTime();
    const timer = setInterval(checkTime, 10000);
    getLocation();
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isWindowOpen && !image) startCamera();
  }, [isWindowOpen, image]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) { console.error("Camera Error"); }
  };

  const getLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, loading: false }),
      () => setLocation({ loading: false }),
      { enableHighAccuracy: true }
    );
  };

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    setImage(canvas.toDataURL('image/png'));
  };

  if (!isWindowOpen) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6 text-center">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl">
          <Clock className="mx-auto text-orange-500 mb-4" size={48} />
          <h2 className="text-2xl font-bold">Verification Closed</h2>
          <p className="text-gray-500 mt-2 italic text-sm">Scheduled: 11:00-11:15, 12:00-12:15, etc.</p>
          <button onClick={onLogout} className="mt-6 text-indigo-600 font-semibold underline">Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-slate-900 p-6 text-white flex justify-between items-center">
          <div><h1 className="text-lg font-bold uppercase tracking-tight">Identity Check</h1><p className="text-xs text-slate-400 font-mono tracking-widest">{user?.name}</p></div>
          <button onClick={onLogout} className="text-xs bg-red-600/20 text-red-400 border border-red-400 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition-all">Logout</button>
        </div>
        <div className="p-6 space-y-6">
          <div className="relative aspect-square bg-black rounded-xl overflow-hidden border-4 border-slate-100">
            {!image ? <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" /> : <img src={image} className="w-full h-full object-cover" />}
            <canvas ref={canvasRef} className="hidden" />
          </div>
          <div className="p-3 bg-gray-50 rounded-lg text-xs font-mono text-center border border-dashed border-gray-300">
             <div className="flex items-center justify-center gap-2 text-gray-500 mb-1"><MapPin size={12}/> LOCATION STAMP</div>
            {location.loading ? "FETCHING GPS..." : `LAT: ${location.lat?.toFixed(5)} | LNG: ${location.lng?.toFixed(5)}`}
          </div>
          {!image ? (
            <button onClick={capturePhoto} className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 shadow-lg shadow-indigo-100"><Camera size={20}/> Capture Photo</button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => setImage(null)} className="flex-1 py-4 bg-gray-100 rounded-xl font-bold text-gray-600 hover:bg-gray-200">Retake</button>
              <button onClick={() => alert("Verification Sent Successfully")} className="flex-1 py-4 bg-emerald-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-emerald-700 shadow-lg shadow-emerald-100"><CheckCircle size={20}/> Submit</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- MAIN LOGIN FORM ---
const LoginForm = ({ setAuthUser }) => {
  const [formData, setFormData] = useState({ name: '', mobile: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const user = USERS_DATA.find(u => u.name === formData.name && u.mobile === formData.mobile && u.password === formData.password);
    if (user) {
      setAuthUser(user);
      navigate('/Identity-Verification');
    } else {
      setError("Incorrect details. Access Denied.");
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#f3f4f6] px-4">
      <div className="w-full max-w-md p-10 bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100">
        <h2 className="text-3xl font-extrabold text-gray-900 text-center tracking-tighter">STAFF PORTAL</h2>
        <p className="text-center text-gray-400 text-xs mt-2 font-medium">Identity Verification Required</p>
        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          {error && <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl text-center font-bold italic">{error}</div>}
          
          <div className="relative">
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Account Name</label>
            <div className="relative">
                <span className="absolute left-4 top-3.5 text-gray-400"><User size={18}/></span>
                <select name="name" required value={formData.name} onChange={handleChange} className="w-full py-3.5 pl-12 pr-10 bg-gray-50 border border-gray-200 rounded-2xl appearance-none outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-medium text-gray-700">
                <option value="" disabled>Choose name</option>
                {USERS_DATA.map((u, i) => <option key={i} value={u.name}>{u.name}</option>)}
                </select>
                <span className="absolute right-4 top-4 text-gray-400 pointer-events-none"><ChevronDown size={18}/></span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Mobile Number</label>
            <div className="relative">
                <span className="absolute left-4 top-3.5 text-gray-400"><Phone size={18}/></span>
                <input type="tel" name="mobile" required placeholder="000-000-0000" className="w-full py-3.5 pl-12 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-medium" value={formData.mobile} onChange={handleChange} />
            </div>
          </div>

          <div className="relative">
            <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Password</label>
            <div className="relative">
                <span className="absolute left-4 top-3.5 text-gray-400"><Lock size={18}/></span>
                <input type={showPassword ? "text" : "password"} name="password" required placeholder="••••••••" className="w-full py-3.5 pl-12 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 font-medium" value={formData.password} onChange={handleChange} />
                <button type="button" className="absolute right-4 top-3.5 text-gray-400" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
            </div>
          </div>

          <button type="submit" className="w-full py-4 mt-4 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 uppercase tracking-widest text-sm">Enter Portal</button>
        </form>
      </div>
    </div>
  );
};

// --- APP COMPONENT ---
export default function App() {
  const [authUser, setAuthUser] = useState(null);

  return (
    <Routes>
      <Route path="/" element={<LoginForm setAuthUser={setAuthUser} />} />
      <Route 
        path="/Identity-Verification" 
        element={
          authUser ? (
            <IdentityVerification user={authUser} onLogout={() => setAuthUser(null)} />
          ) : (
            <Navigate to="/" replace />
          )
        } 
      />
    </Routes>
  );
}